import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Gift, Users, TrendingUp, Sparkles, Mail, Trophy } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FLAVORS = ['Mango Whey', 'Chocolate Whey', 'Vietnamese Coffee Whey', 'Apple Cinnamon Yeast', 'Chickoo Yeast'];

export default function KBInsiderLanding() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [flavor, setFlavor] = useState('');
  const [loading, setLoading] = useState(false);
  const [sentTo, setSentTo] = useState(null);

  const submitMagicLink = (e) => {
    e.preventDefault();
    if (!email) return toast.error('Please enter your email');
    setLoading(true);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API}/insider/auth/magic-link/request`, true);
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.onload = () => {
      setLoading(false);
      if (xhr.status === 200) {
        const data = JSON.parse(xhr.responseText);
        setSentTo(data.email);
        toast.success('Magic link sent! Check your email.');
      } else if (xhr.status === 409) {
        try {
          const data = JSON.parse(xhr.responseText);
          toast.error(data.detail?.message || 'This email is already a KB Partner.');
        } catch { toast.error('This email is a partner account.'); }
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    };
    xhr.onerror = () => { setLoading(false); toast.error('Network error'); };
    xhr.send(JSON.stringify({ email: email.toLowerCase().trim(), name, flavor }));
  };

  const googleSignin = () => {
    if (flavor) sessionStorage.setItem('insider_flavor', flavor);
    sessionStorage.setItem('insider_signup', '1');
    const redirect = `${window.location.origin}/insider/auth-callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a0a] via-[#111] to-[#1a1210] text-white">
      {/* Hero */}
      <div className="max-w-6xl mx-auto px-6 pt-16 pb-12">
        <img
          src="https://customer-assets-7cd3h4nn.emergentagent.net/job_7955cc59-fc5f-4c01-aff4-bac39fc4dad8/artifacts/n9akq2y0_KB%20-%20Final%20Logo%20%28Updated%29-01.webp"
          alt="Kilobeaters"
          data-testid="kb-logo"
          className="h-14 sm:h-16 w-auto mb-8"
        />
        <div className="flex items-center gap-2 mb-6">
          <Sparkles className="w-6 h-6 text-amber-400" />
          <span className="text-amber-400 font-semibold tracking-widest text-sm uppercase">Insider Program</span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Refer 5 friends.<br />
          Get <span className="text-amber-400">1kg of protein</span><br />
          on the house.
        </h1>
        <p className="mt-6 text-lg text-white/70 max-w-2xl">
          Help your loved ones — friends, family, the people you care about — look good, feel good, and live well for longer. Introduce them to metabolic food, and earn a free bag while you're at it.
        </p>
      </div>

      {/* How it works */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <h2 className="text-2xl font-bold mb-8" style={{ fontFamily: 'Outfit, sans-serif' }}>How it works</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="bg-white/5 border-white/10 p-6 text-white">
            <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center mb-4">
              <Users className="w-6 h-6 text-amber-400" />
            </div>
            <h3 className="font-bold text-lg mb-2">1. Share your link</h3>
            <p className="text-white/60 text-sm">You'll get a personal 5% discount link. Share it with the people you love — friends, family, anyone you want to see healthier.</p>
          </Card>
          <Card className="bg-white/5 border-white/10 p-6 text-white">
            <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center mb-4">
              <TrendingUp className="w-6 h-6 text-amber-400" />
            </div>
            <h3 className="font-bold text-lg mb-2">2. They shop, you track</h3>
            <p className="text-white/60 text-sm">When your 5 referrals combined spend crosses ₹10,000, your reward unlocks. See progress live in your dashboard.</p>
          </Card>
          <Card className="bg-white/5 border-white/10 p-6 text-white">
            <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center mb-4">
              <Gift className="w-6 h-6 text-amber-400" />
            </div>
            <h3 className="font-bold text-lg mb-2">3. Claim your bag</h3>
            <p className="text-white/60 text-sm">We ship you a free 1kg bag of protein in the flavour of your choice. No forms, no fine print.</p>
          </Card>
        </div>
      </div>

      {/* Eligibility strip */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="border border-amber-400/30 bg-amber-400/5 rounded-lg p-5 flex items-start gap-4">
          <Trophy className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <strong className="text-amber-400">How it unlocks:</strong>
            <span className="text-white/80"> Sign up with any email — then verify with the email OR phone number you used to place your Kilobeaters orders (3+ orders needed to activate).</span>
          </div>
        </div>
      </div>

      {/* Signup */}
      <div id="signup" className="max-w-md mx-auto px-6 py-16">
        <Card className="bg-white/5 border-white/10 p-8 text-white" data-testid="insider-signup-card">
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Join KBInsider</h2>
          <p className="text-white/60 text-sm mb-6">
            {sentTo ? `Magic link sent to ${sentTo}. Click the link in your email to sign in.` : 'Any email works — we\'ll verify your Kilobeaters orders after you sign in.'}
          </p>

          {!sentTo && (
            <>
              <form onSubmit={submitMagicLink} className="space-y-4" data-testid="insider-magic-link-form">
                <div>
                  <Label htmlFor="ins-name" className="text-white/80">Your name (optional)</Label>
                  <Input id="ins-name" data-testid="insider-name-input" value={name} onChange={e => setName(e.target.value)} placeholder="Amit Sharma" className="bg-white/5 border-white/10 text-white placeholder:text-white/30" />
                </div>
                <div>
                  <Label htmlFor="ins-email" className="text-white/80">Registered email</Label>
                  <Input id="ins-email" data-testid="insider-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@email.com" required className="bg-white/5 border-white/10 text-white placeholder:text-white/30" />
                </div>
                <div>
                  <Label htmlFor="ins-flavor" className="text-white/80">Preferred flavour (optional)</Label>
                  <select id="ins-flavor" data-testid="insider-flavor-select" value={flavor} onChange={e => setFlavor(e.target.value)} className="w-full mt-2 rounded-md bg-white/5 border border-white/10 text-white p-2">
                    <option value="">-- Choose later --</option>
                    {FLAVORS.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <Button type="submit" disabled={loading} data-testid="insider-magic-link-submit" className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold">
                  <Mail className="w-4 h-4 mr-2" />
                  {loading ? 'Sending...' : 'Email me a magic link'}
                </Button>
              </form>

              <div className="flex items-center gap-3 my-6">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-xs text-white/40">OR</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              <Button onClick={googleSignin} variant="outline" data-testid="insider-google-signin" className="w-full border-white/20 bg-white/5 hover:bg-white/10 text-white">
                Continue with Google
              </Button>
            </>
          )}

          {sentTo && (
            <Button onClick={() => setSentTo(null)} variant="ghost" className="w-full text-white/70">
              Use a different email
            </Button>
          )}

          <p className="text-xs text-white/40 mt-6 text-center">
            By joining, you agree to receive KBInsider updates. No spam. Ever.
          </p>
        </Card>

        <div className="text-center mt-8">
          <button onClick={() => navigate('/login')} className="text-sm text-white/50 hover:text-white/80 underline underline-offset-4">
            Are you a KB Partner? Sign in here →
          </button>
        </div>
      </div>
    </div>
  );
}
